import { TutorialDefinition } from '../TutorialEngine.js';
import { LineRenderOptions } from './LineTutorialAdapter.js';
import { COLORS, colorWithAlpha } from '../../rendering/Renderer.js';

type LineScene = {
    gameState: string;
    renderOptions: LineRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

const scenes: LineScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: '62,5262,5253,4353,4243,3242,2232,2223,1323,1314,1424,2425,2526,2021,2131,3141,4151,5051',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Line, draw a path that\nclears all digits', cell: 'E4', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Start point + targets overview
    {
        gameState: '62',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Line starts from\na rim intersection', cell: 'D0', anchor: 'center' },
            { type: 'text-label', text: 'Click grid rim to move\nthe line start point', point: '05', anchor: 'near-right' },
            { type: 'pulse', point: '62', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: ['62', '61', '30', '50']
        }
    },

    // Scene 3: Drawing segments
    {
        gameState: '62,5262,5253',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click cell edges\nto draw segments', point: '43', anchor: 'below' },
            { type: 'text-label', text: 'Hit all corner targets\nto clear each digit', point: '15', anchor: 'near-right' },
            { type: 'pulse', edge: '4353', color: COLORS.teal },
        ],
        animate: {
            interval: 1000,
            loop: true,
            frames: ['62,5262,5253', '62,5262,5253,4353']
        }
    },

    // Scene 4: Clearing digit 1 and hitting digit 3 early
    {
        gameState: '62,5262,5253,4353,4243,3242,2232,2212',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Targets can be hit early', cell: 'C0', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'All corners hit = digit cleared\nDiamond marks last target', cell: 'D4', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 5: No early clear, no branching
    {
        gameState: '62,5262,5253,4353,4243,3242,2232,2122,2131,3141',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Digits must be cleared in order', cell: 'C0', anchor: 'center', color: COLORS.red },
            { type: 'text-label', text: 'Line cannot branch', cell: 'D4', anchor: 'center', color: COLORS.red },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                '62,5262,5253,4353,4243,3242,2232,2122,4344,2131,3141',
                '62,5262,5253,4353,4243,3242,2232,2122,2131,3141']
        }
    },

    // Scene 6: Edge digits and wrapping
    {
        gameState: '62,5262,5253,4353,4243,3242,2232,2223,1323,1314,1424,2425,2526,2021',
        renderOptions: {},
        annotations: [
            { type: 'highlight', cells: ['A3'], color: colorWithAlpha('amber', 0.2) },
            { type: 'text-label', text: 'Edge digit: only 2 targets', cell: 'A3', anchor: 'far-right' },
            { type: 'text-label', text: 'Line wraps to opposite edge', cell: 'C5', anchor: 'center' },
        ],
    },

    // Scene 7: Connected vs disconnected
    {
        gameState: '62,5262,5253,4353,4243,3242,2223,1323,1314,1424,2425,2526,2021',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Teal = connected to start', cell: 'D3', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Amber = disconnected', cell: 'D5', anchor: 'center', color: COLORS.amber },
        ],
    },

];

/**
 * Line tutorial: 7 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/line.md for detailed scene descriptions.
 */
export const LINE_TUTORIAL: TutorialDefinition = {
    gameType: 'line',
    gridCode: '6/1-3:1E2,2A3,3C1',
    scenes,
};
